import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Search, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { rolesService, userAdminService } from '@services';
import { Role } from '@app-types';
import { formatPersonName, formatUserWithStatus } from '@utils/stringUtils';
import { AdminUser } from '@app-types/admin';
import { DivisionLeadDraft } from '@app-types/division';
import styles from './DivisionLeadsSelector.module.css';

export interface LeadUserInfo {
  name: string;
  email: string;
}

export interface DivisionLeadsSelectorProps {
  /** Persisted leads and not-yet-saved picks both satisfy this shape. */
  selectedLeads: DivisionLeadDraft[];
  onAddLead: (userId: number, userInfo?: LeadUserInfo) => void;
  onRemoveLead: (userId: number) => void;
  disabled?: boolean;
  /** Role users must hold to be assignable (default: 'Executive'). */
  roleName?: string;
  /** Text shown when no one is assigned (default: 'No leads assigned'). */
  emptyText?: string;
  /** Search input placeholder (default: 'Search users to add as lead...'). */
  searchPlaceholder?: string;
  /** Remove-button tooltip (default: 'Remove lead'). */
  removeTitle?: string;
  /** 'single' hides the search once one lead is picked (remove it to pick another). Default 'multi'. */
  mode?: 'single' | 'multi';
  /**
   * Minimum picks the consumer requires. Informational: the selector renders no error of its own;
   * the consumer gates submit. Exposed as `data-min-selections` for the consumer's hint/tests.
   */
  minSelections?: number;
  /** Show candidates and new picks as "Name (Active|Inactive)" (lead-level pickers, #936 R9). */
  showStatus?: boolean;
  /** Prefix for data-test-id hooks: `<id>`, `<id>-search`, `<id>-chip`, `<id>-option-<userId>`. */
  dataTestId?: string;
}

const DivisionLeadsSelector: React.FC<DivisionLeadsSelectorProps> = ({
  selectedLeads,
  onAddLead,
  onRemoveLead,
  disabled = false,
  roleName = 'Executive',
  emptyText = 'No leads assigned',
  searchPlaceholder = 'Search users to add as lead...',
  removeTitle = 'Remove lead',
  mode = 'multi',
  minSelections,
  showStatus = false,
  dataTestId
}) => {
  const formatCandidate = (user: AdminUser): string =>
    showStatus ? formatUserWithStatus(user) : formatPersonName(user);
  const selectionFull = mode === 'single' && selectedLeads.length >= 1;
  const tid = (suffix?: string): string | undefined =>
    dataTestId ? (suffix ? `${dataTestId}-${suffix}` : dataTestId) : undefined;
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch roles to find the required role ID
  const { data: roles } = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: () => rolesService.getRoles(),
    staleTime: 300000 // 5 minutes
  });

  const requiredRoleId = roles?.find((r) => r.name === roleName)?.id;

  // Fetch only users holding the required role
  const { data: usersResponse } = useQuery({
    queryKey: ['users-for-leads', requiredRoleId],
    queryFn: () =>
      userAdminService.getAllUsers(
        {
          is_active: 'true',
          role_ids: requiredRoleId ? [requiredRoleId] : undefined
        },
        1,
        200
      ),
    staleTime: 60000,
    enabled: !!requiredRoleId // Only fetch when we have the role ID
  });

  const allUsers: AdminUser[] = (usersResponse?.data as AdminUser[]) || [];

  // Filter users based on search and exclude already selected
  const availableUsers: AdminUser[] = allUsers
    .filter((user: AdminUser) => !selectedLeads.some((l) => l.user_id === user.id))
    .filter((user: AdminUser) => {
      if (!searchQuery) return true;
      const fullName = formatPersonName(user).toLowerCase();
      const email = user.email?.toLowerCase() || '';
      const query = searchQuery.toLowerCase();
      return fullName.includes(query) || email.includes(query);
    });

  const handleSelectUser = (user: AdminUser) => {
    onAddLead(user.id, { name: formatCandidate(user), email: user.email });
    setSearchQuery('');
    setShowDropdown(false);
  };

  // Calculate dropdown position
  useEffect(() => {
    if (showDropdown && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [showDropdown]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!showDropdown) return;

    const updatePosition = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showDropdown]);

  return (
    <div
      className={styles.divisionLeadsSelector}
      data-test-id={tid()}
      data-mode={mode}
      data-min-selections={minSelections}
    >
      {/* Selected Leads */}
      <div className={styles.selected}>
        {selectedLeads.length === 0 ? (
          <span className={styles.noLeads}>{emptyText}</span>
        ) : (
          selectedLeads.map((lead) => (
            <div key={lead.user_id} className={styles.tag} data-test-id={tid('chip')}>
              <span className={styles.tagName}>{lead.user_name}</span>
              {!disabled && (
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={() => onRemoveLead(lead.user_id)}
                  title={removeTitle}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add Lead Input */}
      {!disabled && !selectionFull && (
        <div className={styles.addContainer} ref={containerRef}>
          <div className={styles.searchWrapper}>
            <Search size={16} className={styles.searchIcon} />
            <input
              ref={inputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              className={styles.input}
              data-test-id={tid('search')}
            />
          </div>

          {showDropdown &&
            (searchQuery || availableUsers.length > 0) &&
            ReactDOM.createPortal(
              <div
                className={styles.dropdown}
                style={{
                  position: 'absolute',
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: `${dropdownPosition.width}px`,
                  zIndex: 2000
                }}
              >
                {availableUsers.length === 0 ? (
                  <div className={styles.noUsers}>No matching users found</div>
                ) : (
                  availableUsers.slice(0, 10).map((user: AdminUser) => (
                    <button
                      type="button"
                      key={user.id}
                      className={styles.option}
                      onClick={() => handleSelectUser(user)}
                      data-test-id={tid(`option-${user.id}`)}
                    >
                      <span className={styles.optionName}>{formatCandidate(user)}</span>
                      <span className={styles.optionEmail}>{user.email}</span>
                    </button>
                  ))
                )}
              </div>,
              document.body
            )}
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showDropdown && !selectionFull && <button type="button" className={styles.overlay} onClick={() => setShowDropdown(false)} aria-label="Close dropdown" />}
    </div>
  );
};

export default DivisionLeadsSelector;
